import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IEmailTemplate } from "../../shared/interfaces/email-template/IEmailTemplate";
import { useNavigate } from "react-router-dom";
import { usePagination } from "../../shared/hooks/usePagination";
import { AdminEmailTemplateService } from "./service";
import { handleError } from "../../shared/functions/handleError";
import { enqueueSnackbar } from "notistack";
import { Box, Button, Card, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import CustomDataGrid from "../../shared/components/CustomDataGrid";
import ConfirmDelete from "../../shared/components/ConfirmDelete";
import { useEmailTemplateColumns } from "./hooks/useEmailTemplateColumns";

const AdminEmailTemplates = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<IEmailTemplate[] | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<
    undefined | IEmailTemplate
  >(undefined);
  const navigate = useNavigate();
  const columns = useEmailTemplateColumns(
    setSelectedTemplate,
    setShowDeleteModal,
  );
  const {
    pagination,
    count,
    setPagination,
    totalPages,
    setTotalPages,
    handleUpdateList,
    setCount,
    changeFilterHandler,
  } = usePagination({
    perPage: 30,
  });

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedTemplate(undefined);
  };

  const deleteHandler = (id?: number) => {
    !!id &&
      AdminEmailTemplateService.delete(id)
        .then(() => {
          handleUpdateList();
          enqueueSnackbar(
            t("toasts.successfullyDeleted", {
              title: selectedTemplate?.name,
            }),
            {
              variant: "success",
            },
          );
          closeDeleteModal();
        })
        .catch(handleError);
  };

  useEffect(() => {
    AdminEmailTemplateService.get({ ...pagination, page: pagination?.page + 1 })
      .then((response) => {
        setCount(response?.count);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
        setData(response.data);
      })
      .catch(handleError);
  }, [pagination, setTotalPages, setCount]);
  return (
    <Card sx={{ marginTop: "30px" }}>
      <Box
        sx={{
          pb: "20px",
        }}
      >
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <TextField
              onChange={(e) => {
                changeFilterHandler("name", e.target.value);
              }}
              label={t("general.searchByName")}
              fullWidth
            />
          </Grid>
          <Grid
            size={{ xs: 12, lg: 9 }}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              variant={"contained"}
              onClick={() => navigate("/email-template/create")}
              sx={{ width: { xs: "100%", lg: "auto" } }}
            >
              <AddCircleOutlineOutlinedIcon sx={{ marginRight: "5px" }} />
              {t("button.addNewTemplate")}
            </Button>
          </Grid>
        </Grid>
      </Box>
      <CustomDataGrid
        columns={columns}
        rows={data || []}
        rowCount={count}
        setPagination={setPagination}
        pagination={pagination}
        loading={data === undefined}
        hasNextPage={totalPages < pagination?.page}
      />

      <ConfirmDelete
        open={showDeleteModal}
        title={selectedTemplate?.name}
        handleClose={closeDeleteModal}
        handleSubmit={() => deleteHandler(selectedTemplate?.id)}
      />
    </Card>
  );
};
export default AdminEmailTemplates;
